import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Form, Formik, FormikHelpers } from 'formik';

import {
  Input,
  PhoneNumberInput,
  Select,
  SubmitButtonsLine,
  TimezoneSelect,
} from '@app/form';
import { SubmitButton, Button, InformationBox } from '@app/core';
import { useToast } from '@app/core';
import { YupValidators } from 'lib/validators';
import { useCurrentClient, useLogout } from '../../../queries';
import { SettingContent } from './components/SettingContent';
import { ClientUpdate, useUpdateClient } from 'queries/auth';
import { supportedLocales } from 'i18n';

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h2,
    marginBottom: theme.marginBase * 6,
  },
  logoutContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
    marginTop: theme.marginBase * 6,
  },
  buttonLine: {
    marginTop: theme.marginBase * 2,
  },
}));

const accountSchema = Yup.object().shape({
  account: Yup.object().shape({
    firstName: YupValidators.isValidName().required(),
    lastName: YupValidators.isValidName().required(),
    email: Yup.string().email().required(),
    timezone: Yup.string(),
  }),
  phoneNumber: YupValidators.isPhoneNumber(),
});

const ssoAccountSchema = Yup.object().shape({
  account: Yup.object().shape({
    timezone: Yup.string(),
  }),
});

type Values = ClientUpdate;

export const EditAccount = () => {
  const classes = useStyles();

  const { data: client } = useCurrentClient();
  const { mutateAsync: updateClient } = useUpdateClient(client?.id);

  const isSsoAccount = client?.account.isSsoAccount;

  const { t } = useTranslation();
  const toast = useToast();

  const { mutate: logout } = useLogout();

  if (!client) {
    return null;
  }

  const capitalize = (value: string) =>
    value.charAt(0).toUpperCase() + value.slice(1);
  const localeOptions = supportedLocales.map((locale) => {
    const language = locale.split('-')[0];

    const label = new Intl.DisplayNames([language], {
      type: 'language',
    }).of(language);

    return {
      label: capitalize(label!),
      value: locale,
    };
  });

  const onSubmit = async (values: Values, helpers: FormikHelpers<Values>) => {
    const localeChanged = values.account.locale !== client.account.locale;

    try {
      const res = await updateClient(values);
      await helpers.setValues(res);
      toast.saved();
      if (localeChanged) {
        window.location.reload();
        return;
      }
    } catch (e) {
      helpers.setStatus(e);
      toast.exception();
    }
    helpers.setSubmitting(false);
  };

  return (
    <SettingContent>
      <Formik
        initialValues={client as ClientUpdate}
        validationSchema={isSsoAccount ? ssoAccountSchema : accountSchema}
        onSubmit={onSubmit}
      >
        <Form>
          <h1 className={classes.title}>
            <Trans i18nKey="settings.account.title" />
          </h1>

          {isSsoAccount && (
            <InformationBox>
              <Trans
                i18nKey="settings.account.ssoInfo"
                components={{
                  a: (
                    <a
                      href="https://platform.bilendi.com/account/personal-information"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
              />
              <SubmitButtonsLine className={classes.buttonLine}>
                <Button
                  href="https://platform.bilendi.com/account/personal-information"
                  target="_blank"
                  rel="noreferrer"
                  text={t('settings.account.ssoEditProfile')}
                  small
                />
              </SubmitButtonsLine>
            </InformationBox>
          )}

          <Input
            name={`account.firstName`}
            label={t('form.account.firstName')}
            placeholder="Ex: Albert"
            disabled={isSsoAccount}
          />
          <Input
            name={`account.lastName`}
            label={t('form.account.lastName')}
            placeholder="Ex: Einstein"
            disabled={isSsoAccount}
          />
          <Input
            name={`account.email`}
            label={t('form.account.email')}
            placeholder="Ex: albert.einstein@gmail.com"
            type="email"
            disabled={isSsoAccount}
          />
          <PhoneNumberInput
            name="phoneNumber"
            label={t('form.client.phoneNumber')}
            disabled={isSsoAccount}
          />
          <TimezoneSelect
            name="account.timezone"
            label={t('form.account.timezone')}
          />
          <Select
            name="account.locale"
            label={t('form.account.locale')}
            options={localeOptions}
          />
          <SubmitButton color="accent">
            <Trans i18nKey="settings.account.submit" />
          </SubmitButton>

          <div className={classes.logoutContainer}>
            <Button
              color="blue"
              variant="transparent"
              type="button"
              text={t('settings.account.cookies')}
              className="cky-banner-element"
            />
            <Button
              color="red"
              variant="transparent"
              type="button"
              onClick={() => logout()}
            >
              <Trans i18nKey="settings.account.logout" />
            </Button>
          </div>
        </Form>
      </Formik>
    </SettingContent>
  );
};
